/**
 * EventsTab - Events Discovery & Filtering
 *
 * Events discovery tab for Activity Page.
 * Adds time filter dropdown (Upcoming/All/Past) to existing functionality.
 *
 * 3-Layer CSS Architecture (Nightfall Theme):
 * - Uses --oro-* semantic tokens (Layer 2) ONLY
 * - text-text-secondary → --text-secondary → --nightfall-text-secondary
 * - bg-oro-surface → --oro-surface → --nightfall-surface
 * - border-oro-border → --oro-border → --nightfall-border
 *
 * Issue: #728 (Activity Page Redesign)
 * Issue: #742 (Club context filtering)
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7B/7C
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EventCard, EmptyState, EventCardSkeleton } from '../../components';
import { useClubEvents } from '../../hooks/useEvents';
import { useClubs } from '../../hooks/useClubs';
import { useRegisterFab } from '../../contexts/PWAContext';
import {
  EVENTS_EMPTY_COPY,
  resolveEventsEmptyState,
  type EventsEmptyPrimaryAction,
} from '../../lib/eventsEmptyState';
import { Plus, AlertTriangle, Calendar } from 'lucide-react';
import type { Event } from '../../components/EventCard';

interface EventsTabProps {
  clubId?: string; // Club context from ActivityPage (#742)
  clubSlug?: string | null; // Club slug for navigation (#1199)
}

// Group events by date
function groupEventsByDate(events: Event[]): Map<string, Event[]> {
  const groups = new Map<string, Event[]>();
  
  events.forEach(event => {
    const date = event.date; // Already formatted as "Mon, Dec 2"
    if (!groups.has(date)) {
      groups.set(date, []);
    }
    groups.get(date)!.push(event);
  });
  
  return groups;
}

export default function EventsTab({ clubId, clubSlug }: EventsTabProps) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'upcoming' | 'all' | 'past'>('upcoming');
  // Use club-filtered events when clubId is available (#742)
  const { data: events, isLoading, error } = useClubEvents(clubId || '');

  // #3059: role/state-aware empty states. Create Event is a leader/owner
  // capability (backend create gate), so we derive the viewer's role from the
  // user-clubs query — the cheapest source already cached app-wide — and never
  // render a primary CTA the API would refuse.
  const { data: clubs, isLoading: clubsLoading } = useClubs();
  const hasClubs = (clubs?.length ?? 0) > 0;
  const isLeaderOfAnyClub =
    clubs?.some((c) => c.role === 'owner' || c.role === 'leader') ?? false;
  // Suppress the PWA install prompt while the create-event FAB is on screen (#3096).
  useRegisterFab(isLeaderOfAnyClub);
  // hasPastEvents uses the same past predicate as the "past" filter below so the
  // "See past events" secondary only appears when that view has content.
  const hasPastEvents = useMemo(
    () => (events ?? []).some((e) => new Date(e.starts_at * 1000) < new Date()),
    [events],
  );

  const handleEmptyPrimary = (action: EventsEmptyPrimaryAction) => {
    if (action === 'create-event') {
      navigate('/events/create', { state: { clubSlug } });
    } else {
      navigate('/clubs');
    }
  };

  // Transform API events to EventCard format and filter
  const displayEvents = useMemo(() => {
    if (!events) return [];
    
    return events
      .filter(event => {
        const eventDate = new Date(event.starts_at * 1000);
        const now = new Date();
        
        if (filter === 'upcoming') return eventDate >= now;
        if (filter === 'past') return eventDate < now;
        return true;
      })
      // #1984: Past events read reverse-chronological (newest first); upcoming/all stay
      // ascending (soonest first). Sort before .map() since the transform drops starts_at.
      // groupEventsByDate is insertion-ordered, so this also flips the date-header order.
      .sort((a, b) =>
        filter === 'past' ? b.starts_at - a.starts_at : a.starts_at - b.starts_at
      )
      .map(event => ({
        id: event.id,
        short_code: event.short_code,
        title: event.name || event.title,
        date: new Date(event.starts_at * 1000).toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        }),
        location: event.location_name || 'Location TBD',
        attendees: event.rsvp_count || 0,
        imageUrl: event.card_image_url || event.hero_image_url || '', // #1962 prefers card-sized variant; COALESCE upstream handles event → club fallback
        rsvp_status: event.rsvp_status ?? null, // #2089: per-user RSVP for "Going / Maybe / Not going" pill
        training_group_name: event.training_group_name ?? null, // #2089: training-group badge
        event_number: event.event_number ?? null
      }));
  }, [events, filter]);

  // Group events by date for separators
  const groupedEvents = useMemo(() => groupEventsByDate(displayEvents), [displayEvents]);

  return (
    <div className="px-4 py-6 space-y-6">
      {/* Time Filter Dropdown - 3-layer CSS: bg-oro-surface, text-text-primary, border-oro-border */}
      <div className="flex items-center gap-3">
        <label htmlFor="time-filter" className="text-text-secondary text-sm font-medium">
          Show:
        </label>
        <select
          id="time-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="px-3 py-2 bg-oro-surface text-text-primary border border-oro-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent transition-all"
        >
          <option value="upcoming">Upcoming Events</option>
          <option value="all">All Events</option>
          <option value="past">Past Events</option>
        </select>
      </div>

      {/* Loading State — also wait on the clubs query so the empty state and FAB
          resolve the viewer's role before rendering (never flash a refused CTA). */}
      {(isLoading || clubsLoading) && (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <EventCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="py-12">
          <EmptyState
            icon={<AlertTriangle className="w-12 h-12" />}
            title="Couldn't load events"
            message={error instanceof Error ? error.message : 'Something went wrong'}
            actionLabel="Try again"
            onAction={() => window.location.reload()}
          />
        </div>
      )}

      {/* Empty State — role/state-aware (#3059) */}
      {!isLoading && !clubsLoading && !error && displayEvents.length === 0 && (
        <div className="py-12">
          {filter === 'past' ? (
            <EmptyState
              icon={<Calendar className="w-12 h-12" />}
              title={EVENTS_EMPTY_COPY.pastEmptyTitle}
              message={EVENTS_EMPTY_COPY.pastEmptyMessage}
            />
          ) : (
            (() => {
              const emptyState = resolveEventsEmptyState({
                isLeaderOfAnyClub,
                hasClubs,
                hasPastEvents,
                supportsPastFilter: true,
              });
              return (
                <EmptyState
                  icon={<Calendar className="w-12 h-12" />}
                  title={emptyState.title}
                  message={emptyState.message}
                  actionLabel={emptyState.primary?.label}
                  onAction={
                    emptyState.primary
                      ? () => handleEmptyPrimary(emptyState.primary!.action)
                      : undefined
                  }
                  secondaryActionLabel={emptyState.secondary?.label}
                  onSecondaryAction={
                    emptyState.secondary ? () => setFilter('past') : undefined
                  }
                />
              );
            })()
          )}
        </div>
      )}

      {/* Event List with Date Separators - 3-layer CSS for date headers */}
      {!isLoading && !clubsLoading && !error && displayEvents.length > 0 && (
        <div className="space-y-6">
          {Array.from(groupedEvents.entries()).map(([date, dateEvents]) => (
            <div key={date} className="space-y-3">
              {/* Date Separator - Uses text-text-muted */}
              <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider">
                {date}
              </h2>
              
              {/* Events for this date */}
              <div className="space-y-3">
                {dateEvents.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onClick={() => navigate(`/event/${event.short_code || event.id}`)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Floating Create Button — only for leaders/owners, who can actually
          create events (#3059). Never render a FAB the API would refuse. */}
      {isLeaderOfAnyClub && (
        <button
          onClick={() => navigate('/events/create', { state: { clubSlug } })}
          className="fixed bottom-24 right-4 w-11 h-11 bg-accent hover:bg-accent/90 text-black rounded-2xl shadow-float flex items-center justify-center transition-all duration-200 hover:shadow-float-lg"
          aria-label="Create event"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
